// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { bracketsToStrong } from '@automator/builder/components/utilities';

// Import global utils
import { sortArrayAlphabetically } from '@automator/shared/utils/sort-array-alphabetically';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import definitions
import { filterIntegrations } from '@automator/builder/definitions/integrations';
import { isIntegrationAvailable, integrationHasLoopFilters } from '@automator/builder/definitions/integrations/_filters';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-loop-filter-directory' )
export class AutomatorBuilderItemLoopFilterDirectory extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The unique ID of this loop block
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// Information about what has to be iterated (e.g. users, posts, tokens)
	@property( { type: Object, attribute: false } ) accessor iterableExpression;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="filter-directory">
				${ this._templateInstructions }
				${ this._templateFilterList }
			</div>
		`;
	}

	/**
	 * Renders instructions at the top of the dialog
	 *
	 * @return {TemplateResult|undefined} The instruction block
	 */
	get _templateInstructions() {
		// Add the instructions only if the loop is for users
		if ( this.iterableExpression.type !== 'users' ) {
			return;
		}

		return html`
			<uo-alert 
				type="info"
				heading=${ escapeHTML( __( "What's a loop filter?", 'uncanny-automator' ) ) }
			>
				${ escapeHTML( __( 'Loop filters decide which users will be included in the loop. Action filters within the block, on the other hand, choose what actions are performed for each individual user.', 'uncanny-automator' ) ) }
			</uo-alert>
		`;
	}

	/**
	 * Renders the list with the available loop filters
	 *
	 * @return {TemplateResult} The list with the loop filters
	 */
	get _templateFilterList() {
		return html`
			<uo-accordion
				list-search
				
				.searchPlaceholder="${ escapeHTML( __( 'Search loop filters', 'uncanny-automator' ) ) }"
				.noResultsText="${ escapeHTML( __( 'No results found', 'uncanny-automator' ) ) }"
			>
				${ this._templateFilterListItems }
			</uo-accordion>
		`;
	}

	/**
	 * Returns the loop filters grouped by integration
	 *
	 * @return {TemplateResult} The list of loop filters
	 */
	get _templateFilterListItems() {
		return repeat(
			// Get integrations with conditions
			sortArrayAlphabetically(
				Object.entries( this.integrationsWithLoopFilters ),
				'[1].name'
			),
			// eslint-disable-next-line no-unused-vars
			( [ integrationCode, integration ] ) => integrationCode,
			// eslint-disable-next-line no-unused-vars
			( [ integrationCode, integration ] ) => html`
				<uo-accordion-item>
					<div slot="summary">
						<div class="filter-directory-integration">
							<uo-icon integration=${ integrationCode }></uo-icon> ${ integration.name }
						</div>
					</div>

					${ repeat(
						sortArrayAlphabetically(
							Object.entries( integration.loop_filters ),
							'[1].sentence_readable'
						),
						// eslint-disable-next-line no-unused-vars
						( [ loopFilterCode, loopFilter ] ) => loopFilterCode,
						// eslint-disable-next-line no-unused-vars
						( [ loopFilterCode, loopFilter ] ) => {
							// Check if it's a loop filter of the type of the iterable expression
							if ( loopFilter.loop_type !== this.iterableExpression.type ) {
								return;
							}

							return html`
								<uo-accordion-item-li
									@click=${ event => this.selectLoopFilter( integrationCode, loopFilterCode ) }
								>
									${ bracketsToStrong( loopFilter.sentence ) }
								</uo-accordion-item-li>
							`
						}
					) }

				</uo-accordion-item>
			`
		);
	}

	/**
	 * Handles the selection of the loop filter
	 *
	 * @param {String} integrationCode The integration code of the loop filter
	 * @param {String} loopFilterCode The loop filter code
	 */
	selectLoopFilter( integrationCode, loopFilterCode ) {
		this.dispatchEvent(
			new CustomEvent(
				'uap-builder-loop-filter-directory-selected',
				{
					detail: {
						loopID: this.loopID,
						integrationCode: integrationCode,
						loopFilterCode: loopFilterCode
					}
				}
			)
		);
	}

	/**
	 * Returns an object with the integrations that have loop filters
	 *
	 * @return {Object} Integrations, key is the integration ID
	 */
	get integrationsWithLoopFilters() {
		return filterIntegrations( [
			isIntegrationAvailable,
			// Filter integrations that have loop filters of the type of the iterable expression
			integration => integrationHasLoopFilters(
				integration,
				this.iterableExpression.type
			)
		] );
	}
}